<template>
  <div>
    <div class="pa-0 ma-0">
      <div class="body-overall">
        <v-row style="padding-top:64px!important"></v-row>
        <div :class="getGextPanel" class="pt-6" style="margin:0px!important;">
          <div class="fonts-white container pl-0 pr-1">
            <div class="overlay-grey-web">
              <!-- Header Section -->
              <div class="statistics-header">
                <div class="header-content">
                  <v-icon class="header-icon">mdi-chart-box</v-icon>
                  <h1 class="header-title">Statistics</h1>
                </div>
                <p class="header-subtitle">
                  Track your creators, artists, and music performance
                </p>
              </div>

              <v-divider class="my-4" style="opacity:0.3;"></v-divider>

              <!-- Creators and Artists Section -->
              <div class="statistics-section">
                <h2 class="section-title">
                  <v-icon left>mdi-account-music</v-icon>
                  Creators and Artists
                </h2>

                <div class="creators-empty-state">
                  <div class="empty-icon-wrapper">
                    <v-icon size="64" color="grey">mdi-account-group-outline</v-icon>
                  </div>
                  <h3 class="empty-title">No Creators or Artists</h3>
                  <p class="empty-subtitle">
                    Add creators and artists to start tracking their statistics
                  </p>
                </div>
              </div>

              <v-divider class="my-4" style="opacity:0.3;"></v-divider>

              <!-- Date Range Filters -->
              <div class="statistics-section">
                <h2 class="section-title">
                  <v-icon left>mdi-filter</v-icon>
                  Filter Data
                </h2>

                <div class="date-filters-card">
                  <v-checkbox
                    v-model="allStats"
                    color="white"
                    class="all-data-checkbox"
                    hide-details
                  >
                    <template v-slot:label>
                      <span class="checkbox-label">Show All Data</span>
                    </template>
                  </v-checkbox>

                  <div class="date-range-picker">
                    <v-menu
                      :disabled="allStats"
                      v-model="menuFrom"
                      :close-on-content-click="false"
                      transition="scale-transition"
                    >
                      <template v-slot:activator="{ props }">
                        <v-text-field
                          v-model="formattedDateFrom"
                          label="From Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          v-bind="props"
                          :disabled="allStats"
                          class="date-field"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                        >
                          <template v-slot:append-inner>
                            <v-btn
                              v-if="!allStats"
                              icon
                              size="x-small"
                              variant="text"
                              @click.stop="menuFrom = false"
                            >
                              <v-icon size="16">mdi-close</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="dateFrom"
                        :max="dateTo"
                        @update:model-value="menuFrom = false"
                        color="white"
                        style="border:1px solid rgba(255,255,255,0.3);"
                      ></v-date-picker>
                    </v-menu>

                    <v-menu
                      :disabled="allStats"
                      v-model="menuTo"
                      :close-on-content-click="false"
                      transition="scale-transition"
                    >
                      <template v-slot:activator="{ props }">
                        <v-text-field
                          v-model="formattedDateTo"
                          label="To Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          v-bind="props"
                          :disabled="allStats"
                          class="date-field"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                        >
                          <template v-slot:append-inner>
                            <v-btn
                              v-if="!allStats"
                              icon
                              size="x-small"
                              variant="text"
                              @click.stop="menuTo = false"
                            >
                              <v-icon size="16">mdi-close</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="dateTo"
                        :min="dateFrom"
                        :max="new Date()"
                        @update:model-value="menuTo = false"
                        color="white"
                        style="border:1px solid rgba(255,255,255,0.3);"
                      ></v-date-picker>
                    </v-menu>
                  </div>
                </div>
              </div>

              <v-divider class="my-4" style="opacity:0.3;"></v-divider>

              <!-- Summary Section -->
              <div class="statistics-section">
                <h2 class="section-title">
                  <v-icon left>mdi-chart-line</v-icon>
                  Summary
                </h2>

                <v-alert
                  type="info"
                  variant="outlined"
                  class="summary-alert"
                  icon="mdi-information"
                >
                  <div class="alert-content">
                    <div class="alert-title">Choose Creator or Artist</div>
                    <div class="alert-text">
                      Select a creator or artist from the list to view their performance summary
                    </div>
                  </div>
                </v-alert>

                <div class="summary-empty-state">
                  <div class="empty-icon-wrapper">
                    <v-icon size="64" color="grey">mdi-chart-bar</v-icon>
                  </div>
                  <h3 class="empty-title">No Data Available</h3>
                  <p class="empty-subtitle">
                    Summary statistics will appear here once you select a creator or artist
                  </p>
                </div>
              </div>

              <v-divider class="my-4" style="opacity:0.3;"></v-divider>

              <!-- Tracks Analytics Section -->
              <div class="statistics-section">
                <h2 class="section-title">
                  <v-icon left>mdi-music-note</v-icon>
                  Track Analytics
                </h2>

                <div class="tracks-analytics-layout">
                  <!-- Tracks Sidebar -->
                  <div class="tracks-sidebar">
                    <div class="sidebar-header">
                      <h3 class="sidebar-title">Tracks</h3>
                    </div>

                    <v-text-field
                      v-model="searchQuery"
                      placeholder="Search tracks..."
                      prepend-inner-icon="mdi-magnify"
                      density="comfortable"
                      hide-details
                      clearable
                      class="search-field mb-3"
                      variant="outlined"
                      disabled
                    ></v-text-field>

                    <div class="tracks-empty">
                      <v-icon size="48" color="grey">mdi-music-note-off</v-icon>
                      <p>No tracks available</p>
                    </div>
                  </div>

                  <!-- Track Details -->
                  <div class="track-details-content">
                    <div class="content-empty-state">
                      <div class="empty-icon-wrapper">
                        <v-icon size="80" color="grey">mdi-playlist-music</v-icon>
                      </div>
                      <h3>No Track Selected</h3>
                      <p>Select a track from the list to view detailed analytics</p>
                      <div class="empty-features">
                        <div class="feature-item">
                          <v-icon size="20" color="grey">mdi-play-circle</v-icon>
                          <span>Play Count</span>
                        </div>
                        <div class="feature-item">
                          <v-icon size="20" color="grey">mdi-heart</v-icon>
                          <span>Favorites</span>
                        </div>
                        <div class="feature-item">
                          <v-icon size="20" color="grey">mdi-share</v-icon>
                          <span>Shares</span>
                        </div>
                        <div class="feature-item">
                          <v-icon size="20" color="grey">mdi-chart-timeline</v-icon>
                          <span>Trends</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        <v-row
          :style="(this.isMobileXs || this.isMobilePlayer3) ? 'padding-top:170px!important' : 'padding-top:123px!important'"
        ></v-row>
      </div>
    </div>
  </div>
</template>

<script>
import { useMobile } from '../composables/useMobile.js'
import { useMobilePlayer3 } from '../composables/useMobilePlayer3.js'
import { useMobileXs } from '../composables/useMobileXs.js'
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";


export default {
  setup() {
    const isMobile = useMobile()
    const isMobileXs = useMobileXs()
    const isMobilePlayer3 = useMobilePlayer3();

    const notify = (message) => {
      toast(message, {
        theme: "dark",
        type: "success",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 1500,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyError = (message) => {
      toast(message, {
        theme: "dark",
        type: "error",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    return { isMobile, isMobileXs, notify, notifyError, isMobilePlayer3 }
  },
  name: 'Statistics',

  components: {

  },
  data: () => ({
    navbar: false,
    user: null,
    searchQuery: "",
    menuFrom: false,
    menuTo: false,
    dateFrom: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    dateTo: new Date(),
    allStats: true,

  }),
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {

  },
  destroyed() {
  },
  watch: {
    '$store.state.user': function () {
      this.user = this.$store.getters.getUserData;
    },
  },
  computed: {
    isfullScreen() {
      if (this.isMobile && this.isMobileXs) {
        return false;
      } else {
        return true;
      }
    },
    isLogged() {
      return this.$store.getters.getUserData != null
    },
    isDesktop() {
      if (!this.navbar) {
        return true;
      } else {
        return false;
      }
    },
    getGextPanel() {
      if (this.navbar) {
        if (this.isMobileXs) {
          return 'textPanelMobileXs'
        } else {
          return 'textPanelMobile';
        }
      } else return 'textPanel';
    },
    formattedDateFrom() {
      return this.dateFrom.toLocaleDateString();
    },
    formattedDateTo() {
      return this.dateTo.toLocaleDateString();
    },

  },
  created() {
    if (this.$store.getters.getUserData != null) {
      this.user = this.$store.getters.getUserData;

      if (window.innerWidth > 768) {
        this.navbar = false;
      } else {
        this.navbar = true;
      }

    }
  },
  methods: {

    checkScreenSize() {
      if (window.innerWidth > 768) {
        this.navbar = false;
      } else {
        this.navbar = true;
      }
    },
    handleClickMenu(link) {
      if (link === 'TryMuzE') {
        window.location.href = 'https://muze.muzible.com/';
      } else if (link === 'TryVerse') {
        window.location.href = 'https://verse.muzible.com/';
      } else {
        if (this.$route.name !== link) {
          this.$router.push({ name: link });
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use './css/styles.scss';

/* Statistics Header */
.statistics-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.header-icon {
  font-size: 48px !important;
  color: white;
}

.header-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 42px;
  font-weight: 700;
  color: white;
  margin: 0;
}

.header-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  line-height: 1.5;
}

/* Section */
.statistics-section {
  margin-bottom: 40px;
}

.section-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 28px;
  font-weight: 600;
  color: white;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Creators Empty State */
.creators-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 40px 20px;
  text-align: center;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.5), rgba(18, 18, 18, 0.5));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
}

.empty-icon-wrapper {
  margin-bottom: 20px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.empty-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin-bottom: 8px;
}

.empty-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0;
  max-width: 400px;
  line-height: 1.5;
}

/* Date Filters Card */
.date-filters-card {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.95), rgba(18, 18, 18, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.date-filters-card:hover {
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.all-data-checkbox {
  margin-bottom: 16px;
}

:deep(.all-data-checkbox .v-label) {
  color: white !important;
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 16px !important;
  font-weight: 500;
}

.checkbox-label {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.date-range-picker {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 600px) {
  .date-range-picker {
    grid-template-columns: 1fr;
  }
}

.date-field {
  font-family: "Darker Grotesque", sans-serif !important;
}

:deep(.date-field .v-field) {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

:deep(.date-field .v-field:hover:not(.v-field--disabled)) {
  border-color: rgba(255, 255, 255, 0.4) !important;
}

:deep(.date-field .v-field--focused) {
  border-color: rgba(255, 255, 255, 0.6) !important;
}

:deep(.date-field .v-label) {
  color: rgba(255, 255, 255, 0.7) !important;
  font-family: "Darker Grotesque", sans-serif !important;
}

:deep(.date-field .v-field__input) {
  color: white !important;
  font-family: "Darker Grotesque", sans-serif !important;
  font-weight: 500;
}

:deep(.date-field.v-field--disabled .v-field) {
  opacity: 0.5;
}

/* Summary Alert */
.summary-alert {
  background: rgba(33, 150, 243, 0.1) !important;
  border: 1px solid rgba(33, 150, 243, 0.4) !important;
  border-radius: 12px !important;
  padding: 16px !important;
  margin-bottom: 24px;

  :deep(.v-alert__content) {
    color: white !important;
  }
}

.alert-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.alert-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.5px;
}

.alert-text {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

/* Summary Empty State */
.summary-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 250px;
  padding: 40px 20px;
  text-align: center;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.5), rgba(18, 18, 18, 0.5));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
}

/* Tracks Analytics Layout */
.tracks-analytics-layout {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 24px;
  min-height: 500px;
}

@media (max-width: 1024px) {
  .tracks-analytics-layout {
    grid-template-columns: 1fr;
  }
}

/* Tracks Sidebar */
.tracks-sidebar {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.95), rgba(18, 18, 18, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.tracks-sidebar:hover {
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.sidebar-header {
  margin-bottom: 16px;
}

.sidebar-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: white;
  margin: 0;
}

.search-field {
  font-family: "Darker Grotesque", sans-serif !important;
}

:deep(.search-field .v-field) {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

:deep(.search-field .v-field:hover:not(.v-field--disabled)) {
  border-color: rgba(255, 255, 255, 0.4) !important;
}

:deep(.search-field .v-field--focused) {
  border-color: rgba(255, 255, 255, 0.6) !important;
}

:deep(.search-field .v-field__input) {
  color: white !important;
  font-family: "Darker Grotesque", sans-serif !important;
  font-weight: 500;
}

:deep(.search-field.v-field--disabled .v-field) {
  opacity: 0.5;
}

.tracks-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
}

.tracks-empty p {
  margin-top: 12px;
  font-size: 14px;
}

/* Track Details Content */
.track-details-content {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.95), rgba(18, 18, 18, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.track-details-content:hover {
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.content-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 500px;
}

.content-empty-state h3 {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: white;
  margin: 20px 0 12px 0;
}

.content-empty-state p {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 32px;
  line-height: 1.6;
}

.empty-features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
}

@media (max-width: 600px) {
  .empty-features {
    grid-template-columns: 1fr;
  }
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.feature-item span {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.creators-empty-state,
.date-filters-card,
.summary-empty-state,
.tracks-analytics-layout {
  animation: slideIn 0.4s ease forwards;
}

.summary-alert {
  animation: slideIn 0.3s ease forwards;
}

/* Override Vuetify */
:deep(.v-btn:hover) > .v-btn__overlay {
  --v-hover-opacity: 0.1;
  opacity: 0.1;
}

:deep(.v-list-item:hover) > .v-list-item__overlay {
  --v-hover-opacity: 0.1;
  opacity: 0.1;
}

/* Old animations cleanup */
@keyframes blink {
  0% { opacity: 1; }
  40% { opacity: 1; }
  60% { opacity: 0; }
  100% { opacity: 0; }
}

.blink {
  animation: blink 1.5s infinite both;
}
</style>